import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link, useNavigate } from 'react-router-dom'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { logout } from '../actions/AdminAction'
import { NavMenu, UserLogoutButton } from './layout/NavMenu'
import { BrandMark, Wordmark, ByPokerLauncher } from '../ui/Brand'
import { cx } from '../ui/format'

const COLLAPSE_KEY = 'cm-sidebar-collapsed'

const readCollapsed = () => {
  try {
    return localStorage.getItem(COLLAPSE_KEY) === '1'
  } catch {
    return false
  }
}

/** Desktop sidebar (hidden below the sm breakpoint; MobileNav takes over there). */
const Sidebar = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [role, setRole] = useState({})
  const [collapsed, setCollapsed] = useState(readCollapsed)

  const { userInfo } = useSelector((state) => state.adminLogin)
  const { loadingGetRole, errorGetRole, getRoleData } = useSelector((state) => state.getRoleInfo)

  useEffect(() => {
    if (userInfo) {
      setName(userInfo.name || '')
      if (userInfo.role && !getRoleData) setRole(userInfo.role)
    }
  }, [userInfo, getRoleData])

  const handleLogout = () => {
    dispatch(logout())
    navigate('/')
  }

  // Fresh role from the server wins; an auth failure on that fetch signs the user out.
  useEffect(() => {
    if (getRoleData) {
      setRole(getRoleData)
    } else if (errorGetRole && userInfo) {
      console.warn('getRole error:', errorGetRole)
      if (
        errorGetRole === 'Not authenticated' ||
        (typeof errorGetRole === 'string' && (errorGetRole.includes('401') || errorGetRole.includes('Unauthorized')))
      ) {
        handleLogout()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getRoleData, errorGetRole, userInfo])

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1')
      } catch {
        /* ignore */
      }
      return !c
    })
  }

  return (
    <aside
      className={cx(
        'hidden sm:flex flex-col shrink-0 h-screen sticky top-0 bg-cm-surface border-r border-cm-border transition-[width] duration-200',
        collapsed ? 'w-[72px]' : 'w-64',
      )}
      aria-label="Sidebar"
    >
      <div className={cx('flex items-center h-16 border-b border-cm-border shrink-0', collapsed ? 'justify-center px-2' : 'justify-between px-4')}>
        <Link to="/dashboard" className="flex items-center gap-2.5 min-w-0" aria-label="ChipMonk home">
          <BrandMark size={collapsed ? 36 : 34} />
          {!collapsed && <Wordmark height={15} />}
        </Link>
        {!collapsed && (
          <button
            type="button"
            onClick={toggleCollapsed}
            className="w-8 h-8 grid place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text"
            aria-label="Collapse sidebar"
            title="Collapse sidebar"
          >
            <PanelLeftClose size={16} />
          </button>
        )}
      </div>

      <div className="flex-1 overflow-y-auto py-3">
        {collapsed && (
          <button
            type="button"
            onClick={toggleCollapsed}
            className="w-10 h-10 mx-auto mb-2 grid place-items-center rounded-cm text-cm-muted hover:bg-cm-surface-2 hover:text-cm-text"
            aria-label="Expand sidebar"
            title="Expand sidebar"
          >
            <PanelLeftOpen size={16} />
          </button>
        )}
        <NavMenu role={role} loading={loadingGetRole} collapsed={collapsed} />
      </div>

      <div className={cx('border-t border-cm-border shrink-0', collapsed ? 'p-2' : 'p-3')}>
        <UserLogoutButton name={name} roleName={role?.name} onLogout={handleLogout} collapsed={collapsed} />
        {!collapsed && (
          <div className="flex justify-center pt-3">
            <ByPokerLauncher height={20} />
          </div>
        )}
      </div>
    </aside>
  )
}

export default Sidebar
